import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  BookOpen,
  ChevronDown,
  FileText,
  FolderOpen,
  Library,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import {
  isBookCollectionKey,
  knowledgeBaseLabel,
  type KnowledgeBaseRecord,
} from "@/lib/knowledgeBaseRegistry";
import { agentsHref } from "@/lib/atwAgentsHost";
import { knowledgeBasePath, knowledgeLibrariesPath } from "@/lib/knowledgePaths";
import {
  KnowledgeChrome,
  KnowledgeError,
  knowledgePrimaryLinkClass,
  knowledgeSecondaryLinkClass,
} from "@/pages/knowledge/KnowledgeChrome";
import { useCustomBooks } from "@/pages/knowledge/useCustomBooks";
import { useCustomLibraries } from "@/pages/knowledge/useCustomLibraries";
import { useKnowledgeArticles } from "@/pages/knowledge/useKnowledgeArticles";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";

const fieldClass =
  "mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-white/20 dark:bg-zinc-900 dark:text-white dark:placeholder:text-zinc-500";

type PanelMode =
  | { kind: "create" }
  | { kind: "edit"; base: KnowledgeBaseRecord }
  | { kind: "delete"; base: KnowledgeBaseRecord };

const overviewCopy =
  "An org can have more than one knowledge base. Libraries and books are optional views inside the active base. Articles share the same store One uses.";

/** Knowledge base home: multi-base switcher, optional hierarchy, shared article store. */
export function KnowledgeOverviewPage() {
  const navigate = useNavigate();
  const { baseId: routeBaseId } = useParams<{ baseId?: string }>();
  const { articles, loading, error, reload: reloadArticles } = useKnowledgeArticles();
  const { books: customBooks } = useCustomBooks();
  const { libraries: customLibraries } = useCustomLibraries();
  const { bases, hasBases, active, activeId, setActive, createBase, editBase, deleteBase } =
    useKnowledgeBases();

  const listActiveId = useMemo(() => {
    const fromRoute = (routeBaseId || "").trim();
    if (fromRoute && bases.some((base) => base.id === fromRoute)) return fromRoute;
    return activeId;
  }, [routeBaseId, bases, activeId]);

  const listActive =
    bases.find((base) => base.id === listActiveId) || active || bases[0] || null;

  const [panel, setPanel] = useState<PanelMode | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [formBusy, setFormBusy] = useState(false);
  const [formNote, setFormNote] = useState<string | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!moreOpen) return;
    function onPointerDown(event: MouseEvent) {
      if (!moreRef.current?.contains(event.target as Node)) setMoreOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setMoreOpen(false);
    }
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  useEffect(() => {
    if (!panel) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape" && !formBusy) {
        setPanel(null);
        setFormError(null);
        setName("");
        setDescription("");
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panel, formBusy]);

  const bookKeys = new Set<string>();
  let standalone = 0;
  for (const article of articles) {
    const keys = article.collectionKeys.filter(isBookCollectionKey);
    if (keys.length === 0) {
      standalone += 1;
      continue;
    }
    for (const key of keys) bookKeys.add(key);
  }
  for (const book of customBooks) bookKeys.add(book.collectionKey);

  const bookCount = bookKeys.size;
  const libraryCount = customLibraries.length;

  function selectBase(baseId: string) {
    if (baseId === listActiveId) return;
    setActive(baseId);
    void navigate(agentsHref(knowledgeBasePath(baseId)));
  }

  function openLibraries(baseId: string) {
    setActive(baseId);
    void navigate(agentsHref(knowledgeLibrariesPath(baseId)));
  }

  function openCreate() {
    setPanel({ kind: "create" });
    setFormError(null);
    setFormNote(null);
    setName("");
    setDescription("");
  }

  function openEdit(base: KnowledgeBaseRecord) {
    setPanel({ kind: "edit", base });
    setFormError(null);
    setFormNote(null);
    setName(base.name);
    setDescription(base.description || "");
  }

  function openDelete(base: KnowledgeBaseRecord) {
    setPanel({ kind: "delete", base });
    setFormError(null);
    setFormNote(null);
  }

  function closePanel() {
    setPanel(null);
    setFormError(null);
    setName("");
    setDescription("");
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setFormNote(null);
    setFormBusy(true);
    const result = await createBase(name, description);
    setFormBusy(false);
    if (!result.ok) {
      setFormError(result.error);
      return;
    }
    closePanel();
    void navigate(agentsHref(knowledgeBasePath(result.base.id)));
    if (!result.articleId) {
      setFormNote(
        `Created "${result.base.name}" in this browser. The anchor article did not save, so other devices may not see this empty base until you add an article.`,
      );
    }
  }

  async function onEdit(event: FormEvent) {
    event.preventDefault();
    if (!panel || panel.kind !== "edit") return;
    setFormError(null);
    setFormBusy(true);
    const result = await editBase(panel.base.id, name, description);
    setFormBusy(false);
    if (!result.ok) {
      setFormError(result.error);
      return;
    }
    setFormNote(`Saved changes to "${result.base.name}".`);
    closePanel();
  }

  async function onDeleteConfirm() {
    if (!panel || panel.kind !== "delete") return;
    setFormError(null);
    setFormBusy(true);
    const result = await deleteBase(panel.base.id);
    setFormBusy(false);
    if (!result.ok) {
      setFormError(result.error);
      return;
    }
    const nameDeleted = panel.base.name;
    closePanel();
    await reloadArticles();
    if (result.failed > 0) {
      setFormNote(
        `Deleted "${nameDeleted}". Removed membership from ${result.stripped.toLocaleString()} article(s); ${result.failed.toLocaleString()} could not be updated.`,
      );
    } else {
      setFormNote(
        `Deleted "${nameDeleted}". Removed knowledge base membership from ${result.stripped.toLocaleString()} article(s). Article bodies were kept.`,
      );
    }
  }

  const moreMenu = (
    <div ref={moreRef} className="relative">
      <button
        type="button"
        onClick={() => setMoreOpen((value) => !value)}
        className={knowledgeSecondaryLinkClass}
        aria-label="More knowledge links"
        aria-haspopup="menu"
        aria-expanded={moreOpen}
      >
        More
        <ChevronDown className="h-4 w-4" aria-hidden />
      </button>
      {moreOpen ? (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 w-44 origin-top-right rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-white/10 dark:bg-zinc-900"
        >
          <Link
            role="menuitem"
            to={agentsHref("/knowledge/libraries")}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-zinc-100 dark:hover:bg-white/5"
            onClick={() => setMoreOpen(false)}
          >
            <FolderOpen className="h-4 w-4" aria-hidden />
            Libraries
          </Link>
          <Link
            role="menuitem"
            to={agentsHref("/knowledge/books")}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-zinc-100 dark:hover:bg-white/5"
            onClick={() => setMoreOpen(false)}
          >
            <BookOpen className="h-4 w-4" aria-hidden />
            Books
          </Link>
          <Link
            role="menuitem"
            to={agentsHref("/knowledge/articles")}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-zinc-100 dark:hover:bg-white/5"
            onClick={() => setMoreOpen(false)}
          >
            <FileText className="h-4 w-4" aria-hidden />
            Articles
          </Link>
        </div>
      ) : null}
    </div>
  );

  const createKnowledgeBaseButton = (
    <button type="button" onClick={openCreate} className={knowledgePrimaryLinkClass}>
      <Plus className="h-4 w-4" aria-hidden />
      Knowledge Base
    </button>
  );

  const panelModal =
    panel == null ? null : (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !formBusy) closePanel();
        }}
      >
        {panel.kind === "create" ? (
          <form
            onSubmit={(event) => void onCreate(event)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="kb-create-title"
            className="w-full max-w-lg space-y-4 rounded-lg border border-slate-200 bg-white p-5 shadow-lg dark:border-white/10 dark:bg-zinc-950"
          >
            <div>
              <h2
                id="kb-create-title"
                className="text-base font-semibold text-slate-900 dark:text-white"
              >
                New knowledge base
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-zinc-400">
                {hasBases
                  ? "Creates another knowledge base for this org and selects it. A draft anchor article is saved so the base can be rediscovered from existing article APIs (no new Amplify route)."
                  : "Creates the first knowledge base for this org and selects it. A draft anchor article is saved so the base can be rediscovered from existing article APIs (no new Amplify route)."}
              </p>
            </div>
            {formError ? <KnowledgeError message={formError} /> : null}
            <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
              Name
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                autoFocus
                placeholder="Product handbook"
                className={fieldClass}
              />
            </label>
            <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
              Description{" "}
              <span className="font-normal text-slate-500 dark:text-zinc-500">(optional)</span>
              <textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={2}
                placeholder="Who this base is for and what belongs here"
                className={fieldClass}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={formBusy || !name.trim()}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
              >
                {formBusy ? "Creating…" : "Create knowledge base"}
              </button>
              <button
                type="button"
                onClick={closePanel}
                disabled={formBusy}
                className={knowledgeSecondaryLinkClass}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {panel.kind === "edit" ? (
          <form
            onSubmit={(event) => void onEdit(event)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="kb-edit-title"
            className="w-full max-w-lg space-y-4 rounded-lg border border-slate-200 bg-white p-5 shadow-lg dark:border-white/10 dark:bg-zinc-950"
          >
            <div>
              <h2
                id="kb-edit-title"
                className="text-base font-semibold text-slate-900 dark:text-white"
              >
                Edit knowledge base
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-zinc-400">
                Change the display name or description. The membership id stays the same so existing
                articles keep belonging to this base.
              </p>
            </div>
            {formError ? <KnowledgeError message={formError} /> : null}
            <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
              Name
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                autoFocus
                className={fieldClass}
              />
            </label>
            <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
              Description{" "}
              <span className="font-normal text-slate-500 dark:text-zinc-500">(optional)</span>
              <textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={2}
                placeholder="Who this base is for and what belongs here"
                className={fieldClass}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={
                  formBusy ||
                  !name.trim() ||
                  (name.trim() === panel.base.name &&
                    description.trim() === (panel.base.description || "").trim())
                }
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
              >
                {formBusy ? "Saving…" : "Save changes"}
              </button>
              <button
                type="button"
                onClick={closePanel}
                disabled={formBusy}
                className={knowledgeSecondaryLinkClass}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {panel.kind === "delete" ? (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="kb-delete-title"
            className="w-full max-w-lg space-y-4 rounded-lg border border-red-300 bg-white p-5 shadow-lg dark:border-red-400/40 dark:bg-zinc-950"
          >
            <div>
              <h2
                id="kb-delete-title"
                className="text-base font-semibold text-slate-900 dark:text-white"
              >
                Delete knowledge base?
              </h2>
              <p className="mt-2 text-sm text-slate-600 dark:text-zinc-400">
                Delete{" "}
                <span className="font-medium text-slate-900 dark:text-white">{panel.base.name}</span>?
                This removes the base from the shared list and strips its{" "}
                <code className="text-xs">kb:</code> membership from member articles. Article bodies
                and book keys stay in the store. Custom books registered only for this base are
                cleared from this browser. Other knowledge bases are not changed.
              </p>
            </div>
            {formError ? <KnowledgeError message={formError} /> : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={formBusy}
                onClick={() => void onDeleteConfirm()}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
              >
                {formBusy ? "Deleting…" : "Delete knowledge base"}
              </button>
              <button
                type="button"
                disabled={formBusy}
                onClick={closePanel}
                className={knowledgeSecondaryLinkClass}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );

  if (!hasBases) {
    return (
      <KnowledgeChrome
        title="Knowledge bases"
        description="Your org starts with no knowledge bases. Create the first one to organize libraries, books, and articles."
        badge="Markdown"
        showBaseSwitcher={false}
        actions={createKnowledgeBaseButton}
      >
        {error ? <KnowledgeError message={error} /> : null}
        {formError && !panel ? <KnowledgeError message={formError} /> : null}
        {formNote ? (
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 dark:border-white/10 dark:bg-zinc-950/40 dark:text-zinc-300">
            {formNote}
          </div>
        ) : null}

        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center dark:border-white/20 dark:bg-zinc-950/40">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            No knowledge bases yet
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-slate-600 dark:text-zinc-400">
            Nothing is created for you automatically. Add a knowledge base when you are ready, then
            use Libraries, Books, and Articles inside it.
          </p>
          <button
            type="button"
            onClick={openCreate}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
          >
            <Plus className="h-4 w-4" aria-hidden />
            Knowledge Base
          </button>
        </div>
        {panelModal}
      </KnowledgeChrome>
    );
  }

  return (
    <KnowledgeChrome
      title={listActive ? knowledgeBaseLabel(listActive) : "Knowledge base"}
      description={
        listActive?.description?.trim() ||
        "Active knowledge base for this org. Use the overview below to browse libraries, books, and articles."
      }
      badge="Markdown"
      showBaseSwitcher={false}
      actions={
        <>
          {moreMenu}
          {createKnowledgeBaseButton}
        </>
      }
    >
      {error ? <KnowledgeError message={error} /> : null}
      {formError && !panel ? <KnowledgeError message={formError} /> : null}
      {formNote ? (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 dark:border-white/10 dark:bg-zinc-950/40 dark:text-zinc-300">
          {formNote}
        </div>
      ) : null}

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-zinc-500">
            Overview of this knowledge base
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-zinc-400">{overviewCopy}</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <OverviewCard
            icon={FolderOpen}
            title="Libraries"
            value={loading ? "…" : libraryCount.toLocaleString()}
            detail={libraryCount === 0 ? "None until you create one" : "Optional group of books"}
            href="/knowledge/libraries"
          />
          <OverviewCard
            icon={BookOpen}
            title="Books"
            value={loading ? "…" : bookCount.toLocaleString()}
            detail="Optional collection keys"
            href="/knowledge/books"
          />
          <OverviewCard
            icon={FileText}
            title="Articles"
            value={loading ? "…" : articles.length.toLocaleString()}
            detail={
              loading ? "Loading…" : `${standalone.toLocaleString()} standalone (no book)`
            }
            href="/knowledge/articles"
          />
          <OverviewCard
            icon={Library}
            title="Live store"
            value="API"
            detail="/api/knowledge/articles"
            href="/knowledge/articles"
          />
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-zinc-500">
            Your knowledge bases
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-zinc-400">
            Select a base to scope Libraries, Books, and Articles. Double-click a row or use View to
            open its libraries. Edit name or description from this list.
          </p>
        </div>
        <ul className="divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 bg-white dark:divide-white/10 dark:border-white/10 dark:bg-zinc-950/40">
          {bases.map((base) => {
            const selected = base.id === listActiveId;
            return (
              <li
                key={base.id}
                className="flex flex-wrap items-center gap-2 px-3 py-2.5 sm:flex-nowrap"
              >
                <button
                  type="button"
                  onClick={() => selectBase(base.id)}
                  onDoubleClick={() => openLibraries(base.id)}
                  aria-pressed={selected}
                  title="Double-click to open libraries"
                  className={
                    selected
                      ? "min-w-0 flex-1 rounded-md bg-indigo-600 px-3 py-2 text-left"
                      : "min-w-0 flex-1 rounded-md px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-white/5"
                  }
                >
                  <span
                    className={
                      selected
                        ? "block text-sm font-medium text-slate-900 dark:text-white"
                        : "block text-sm font-medium text-slate-800 dark:text-zinc-200"
                    }
                  >
                    {knowledgeBaseLabel(base)}
                  </span>
                  {base.description ? (
                    <span
                      className={
                        selected
                          ? "mt-0.5 block text-xs text-indigo-100"
                          : "mt-0.5 block text-xs text-slate-500 dark:text-zinc-400"
                      }
                    >
                      {base.description}
                    </span>
                  ) : null}
                </button>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => openLibraries(base.id)}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-white/20 dark:text-zinc-200 dark:hover:bg-white/5"
                  >
                    <Library className="h-3.5 w-3.5" aria-hidden />
                    View
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(base)}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-white/20 dark:text-zinc-200 dark:hover:bg-white/5"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => openDelete(base)}
                    className="inline-flex items-center gap-1 rounded-md border border-red-300 px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-400/40 dark:text-red-300 dark:hover:bg-red-950/40"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    Delete
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {panelModal}

      <div className="rounded-lg border border-slate-200 bg-white px-5 py-4 text-sm text-slate-600 dark:border-white/10 dark:bg-zinc-950/40 dark:text-zinc-400">
        <p className="font-medium text-slate-800 dark:text-zinc-200">Optional hierarchy</p>
        <p className="mt-2">
          Create an article with no book. Add books only when you want them. Assigning knowledge to
          an agent comes later on the agent screen, not here.
        </p>
      </div>
    </KnowledgeChrome>
  );
}

function OverviewCard({
  icon: Icon,
  title,
  value,
  detail,
  href,
}: {
  icon: typeof FileText;
  title: string;
  value: string;
  detail: string;
  href: string;
}) {
  return (
    <Link
      to={agentsHref(href)}
      className="flex flex-col rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:bg-zinc-950/40 dark:hover:border-white/20 dark:hover:bg-zinc-900/50"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-zinc-300">
        <Icon className="h-4 w-4 text-slate-500 dark:text-zinc-500" aria-hidden />
        {title}
      </div>
      <p className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">{value}</p>
      <p className="mt-1 text-xs text-slate-500 dark:text-zinc-500">{detail}</p>
    </Link>
  );
}
